import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { Alert, Box, Grid, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { FormTextField } from "components/Forms/FormTextField";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import {
  CreateEpisodicEventInput,
  EpisodicEventItemFragment,
  Issue,
  IssueLevel,
  UpdateEpisodicEventInput,
  WasteEpisodicEventContactMethod,
  WasteEpisodicEventNoticeKind,
  WasteEpisodicEventType,
} from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useWatch, UseFormSetError } from "react-hook-form";
import { getDateLikeIsoDate } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  CREATE_EPISODIC_EVENT_MUTATION,
  DELETE_EPISODIC_EVENT_NOTICE_MUTATION,
  EPISODIC_EVENT_VALIDATOR_QUERY,
  eventRefetchQueries,
  UPDATE_EPISODIC_EVENT_MUTATION,
  UPSERT_EPISODIC_EVENT_NOTICE_MUTATION,
} from "./api";
import { EpaContactSection } from "./EpaContactSection";
import {
  EpisodicNotice,
  episodicWindowEnd,
  isFutureNoticeDate,
  relativeDayLabel,
  requiresEpaContactQuestion,
  resolveFormObligation,
} from "./episodicDisplay";
import { EpisodicEventFormValues } from "./episodicEventFormTypes";
import { useNoticeProofUpload } from "./noticeProof";

const TYPE_RADIO_OPTIONS = [
  { value: WasteEpisodicEventType.Planned, label: "This event is planned" },
  { value: WasteEpisodicEventType.Unplanned, label: "This event is unplanned" },
];

/** Answer-required copy for the 72-hour EPA-contact question — "no" is an
 * acceptable answer, only leaving it blank is blocked (form-only enforcement,
 * see the design doc's Rejected ideas). */
const EPA_CONTACT_REQUIRED_MESSAGE = 'Answer required — "no" is fine';

function withDerivedEndDate(values: EpisodicEventFormValues): EpisodicEventFormValues {
  return { ...values, endDate: episodicWindowEnd(values.startDate) };
}

function toCreateEpisodicEventInput(
  values: EpisodicEventFormValues,
  facilityId: string
): CreateEpisodicEventInput {
  return {
    facilityId,
    type: values.type,
    reason: values.reason.trim(),
    startDate: values.startDate ?? "",
    endDate: values.endDate ?? "",
  };
}

function toUpdateEpisodicEventInput(
  values: EpisodicEventFormValues,
  eventId: string
): UpdateEpisodicEventInput {
  return {
    id: eventId,
    reason: values.reason.trim(),
    startDate: values.startDate ?? undefined,
    endDate: values.endDate ?? undefined,
  };
}

/** The existing EPA_CONTACT notice, if any, prefilled so reopening the edit
 * dialog after the 72-hour window has passed doesn't re-demand an answer
 * that's already on record. Takes the notice array directly (rather than the
 * whole event) so it's testable with plain literals. */
export function epaContactDefaults(
  notices: EpisodicNotice[]
): Pick<EpisodicEventFormValues, "epaContacted" | "epaContactDate" | "epaContactMethod"> {
  const notice = notices.find(
    (candidate) => candidate.kind === WasteEpisodicEventNoticeKind.EpaContact
  );
  if (!notice) {
    return { epaContacted: null, epaContactDate: null, epaContactMethod: null };
  }
  return {
    epaContacted: "yes",
    epaContactDate: getDateLikeIsoDate(notice.noticeDate) ?? null,
    epaContactMethod: notice.method ?? null,
  };
}

/** The proof document already attached to the event's EPA_CONTACT notice, if
 * any — a correction that keeps it needs no fresh upload. */
function epaContactProof(event: EpisodicEventItemFragment | undefined) {
  return (
    event?.notices.find((notice) => notice.kind === WasteEpisodicEventNoticeKind.EpaContact)
      ?.document ?? null
  );
}

/**
 * Debounced live validator, mirroring
 * AccumulationAreas/AccumulationAreaDialog's useAccumulationAreaInputValidation.
 * The validator's `EpisodicEventInput` doesn't carry `reason`, so it never
 * flags a blank reason — that's enforced client-side in `onSubmit` instead.
 *
 * Runs with whatever dates the form has, including neither: the
 * facility-level warnings (eligibility, generator status) don't need a date
 * at all, and neither does the per-year limit, so declare mode's initial
 * trigger (see the dialog's `open` effect) surfaces all three before the user
 * has touched a field. The date-order/window/notice checks stay silent
 * server-side until the dates they need are present.
 */
function useEpisodicEventInputValidation(facilityId: string, currentEventId?: string | null) {
  const [query] = useLazyQuery(EPISODIC_EVENT_VALIDATOR_QUERY, { fetchPolicy: "no-cache" });

  return useCallback(
    async (values: EpisodicEventFormValues): Promise<Issue[]> => {
      const { data } = await query({
        variables: {
          input: {
            facilityId,
            type: values.type,
            startDate: values.startDate ?? undefined,
            endDate: values.endDate ?? undefined,
          },
          currentEventId: currentEventId ?? undefined,
        },
      });
      return data?.episodicEventInputValidator ?? [];
    },
    [query, facilityId, currentEventId]
  );
}

/** The validator can't see `reason` at all, so a blank one never surfaces as
 * a server Issue — catch it here before it reaches the mutation. */
function validateReason(
  values: EpisodicEventFormValues,
  setError: UseFormSetError<EpisodicEventFormValues>
): boolean {
  if (values.reason.trim()) {
    return true;
  }
  setError("reason", { type: "manual", message: "Reason is required" });
  return false;
}

/**
 * Neither `DateField` is marked `required` (so nothing stops the user from
 * clearing one, and no inline error appears on its own), and Save calls
 * `onSubmit` directly rather than through RHF's `handleSubmit`, so nothing
 * else blocks a blank date from reaching the mutation. That matters now that
 * the validator runs — and can return zero issues — before either date is
 * set: `createEpisodicEvent`'s `startDate`/`endDate` are non-null columns, so
 * a blank one reaching it would surface as an opaque scalar-parse error
 * instead of a friendly message. Catch it here, mirroring `validateReason`.
 */
function validateDates(
  values: EpisodicEventFormValues,
  setError: UseFormSetError<EpisodicEventFormValues>
): boolean {
  let valid = true;
  if (!values.startDate) {
    setError("startDate", { type: "manual", message: "Start date is required" });
    valid = false;
  }
  if (!values.endDate) {
    setError("endDate", { type: "manual", message: "End date is required" });
    valid = false;
  }
  return valid;
}

/**
 * Form-only gate for the 72-hour EPA-contact question (see the design doc's
 * Rejected ideas — the mutation itself never requires an answer). Once the
 * window has elapsed, leaving the question blank blocks Save; "no" doesn't.
 * Answering "yes" always requires the date and method that go with it.
 */
export function validateEpaContact(
  values: EpisodicEventFormValues,
  setError: UseFormSetError<EpisodicEventFormValues>
): boolean {
  if (values.type !== WasteEpisodicEventType.Unplanned || !values.startDate) {
    return true;
  }
  if (requiresEpaContactQuestion(values.type, values.startDate) && values.epaContacted === null) {
    setError("epaContacted", { type: "manual", message: EPA_CONTACT_REQUIRED_MESSAGE });
    return false;
  }
  if (values.epaContacted !== "yes") {
    return true;
  }
  let valid = true;
  if (!values.epaContactDate) {
    setError("epaContactDate", { type: "manual", message: "Contact date is required" });
    valid = false;
  } else if (isFutureNoticeDate(values.epaContactDate)) {
    setError("epaContactDate", {
      type: "manual",
      message: "Contact date can't be in the future",
    });
    valid = false;
  }
  if (!values.epaContactMethod) {
    setError("epaContactMethod", { type: "manual", message: "Contact method is required" });
    valid = false;
  }
  return valid;
}

/** BAD_USER_INPUT errors surface their (user-safe) server message verbatim;
 * anything else falls back to a generic toast. */
function describeSaveError(error: unknown, fallback: string): string {
  if (!(error instanceof ApolloError)) {
    return fallback;
  }
  const badUserInput = error.graphQLErrors.find((e) => e.extensions?.code === "BAD_USER_INPUT");
  return badUserInput?.message ?? fallback;
}

/**
 * The one-per-type-per-year limit, which the validator answers from the type
 * alone. It belongs against the radio that provokes it — a user who has just
 * picked "planned" is asking "may I declare a planned event?", and answering
 * that five fields down, under the date pickers, reads as a complaint about
 * the dates.
 */
const YEAR_LIMIT_KEY = "episodicYearLimit";

function YearLimitAlert({ issues }: { issues: Issue[] }) {
  const yearLimit = issues.find((issue) => issue.key === YEAR_LIMIT_KEY);
  if (!yearLimit) {
    return null;
  }
  return (
    <Grid size={{ xs: 12 }}>
      {/* Tucked up against the radio it answers, with the gap moved below it
          so it reads as part of that question rather than the next field's. */}
      <Alert severity="error" sx={{ mt: -1, mb: 1 }}>
        {yearLimit.message}
      </Alert>
    </Grid>
  );
}

/** The validator's 30-day planned-notice advisory (`episodicPlannedNotice`). */
const PLANNED_NOTICE_KEY = "episodicPlannedNotice";

/**
 * The validator judges the 30-day planned-notice rule from the dates alone;
 * it doesn't know whether the event's 8700-12 is already on record. In edit
 * mode the dialog does — the same `resolveFormObligation` verdict the
 * obligations row shows — so a satisfied obligation drops the advisory
 * rather than telling the user to record a filing that already exists.
 */
function withoutSatisfiedFilingWarning(
  issues: Issue[],
  event: EpisodicEventItemFragment | undefined
): Issue[] {
  if (!event || resolveFormObligation(event).kind === "notFiled") {
    return issues;
  }
  return issues.filter((issue) => issue.key !== PLANNED_NOTICE_KEY);
}

/**
 * Renders one severity's worth of validator issues verbatim — no
 * state-specific client copy. CRITICAL issues (`severity="error"`) block
 * save via `hasCriticalIssues`; ERROR issues (`severity="warning"`) are
 * informational only. These keys don't map to a form field, so they render as
 * a standalone list rather than relying on `useValidatingForm`'s per-field
 * error wiring — except the year limit, which `YearLimitAlert` has already
 * placed against the field that does own it.
 */
function IssueAlerts({
  issues,
  level,
  severity,
}: {
  issues: Issue[];
  level: IssueLevel;
  severity: "error" | "warning";
}) {
  const filtered = issues.filter((issue) => issue.level === level && issue.key !== YEAR_LIMIT_KEY);
  if (filtered.length === 0) {
    return null;
  }
  return (
    <Grid size={{ xs: 12 }}>
      <Stack spacing={1}>
        {filtered.map((issue) => (
          <Alert key={`${issue.key ?? level}-${issue.message}`} severity={severity}>
            {issue.message}
          </Alert>
        ))}
      </Stack>
    </Grid>
  );
}

export type NoticeToSave = {
  kind: WasteEpisodicEventNoticeKind;
  noticeDate: string;
  method?: WasteEpisodicEventContactMethod;
  /** A locally picked proof file, uploaded by `useSaveNotices` at save time
   * and attached via the upsert's `documentId`. Absent when no new file was
   * picked — the upsert then omits `documentId` entirely, which leaves any
   * already-attached proof untouched. */
  proofFile?: File;
  /** The user explicitly removed the attached proof: `useSaveNotices` sends
   * `documentId: null` (detach). Never set alongside `proofFile` — a newly
   * picked file supersedes a removal. */
  detachProof?: boolean;
};

/** Whether the EPA-contact answer/date/method actually changed from what the
 * dialog prefilled. An untouched prefilled notice is not re-upserted — the
 * write would be a pointless no-op. (The notice's `note`, which the form has
 * no field for, is safe either way: the upsert treats an absent note as
 * "keep", so even a proof-only re-upsert can't blank it.) */
function epaContactChanged(
  values: EpisodicEventFormValues,
  defaults: EpisodicEventFormValues
): boolean {
  return (
    values.epaContacted !== defaults.epaContacted ||
    values.epaContactDate !== defaults.epaContactDate ||
    values.epaContactMethod !== defaults.epaContactMethod
  );
}

/** The contact notice's proof intent for this save: a picked file attaches
 * (and supersedes a prior removal in the same session), an explicit removal
 * detaches, otherwise the attached proof is left alone. */
function contactProofChange(
  values: EpisodicEventFormValues
): Pick<NoticeToSave, "proofFile" | "detachProof"> {
  if (values.epaContactProofFile) {
    return { proofFile: values.epaContactProofFile };
  }
  if (values.epaContactProofRemoved) {
    return { detachProof: true };
  }
  return {};
}

/**
 * Which notices `saveNotices` should upsert, gated on the answer, the
 * event's current type, AND whether the relevant fields are actually dirty
 * relative to `defaults` (the values the dialog prefilled from
 * `event.notices`). Declaring a brand-new event passes all-null defaults, so
 * any answer is dirty there; editing one only re-upserts a notice whose
 * fields the user actually changed — see `epaContactChanged`'s doc for why
 * that matters. RHF keeps an unmounted section's values around, so a
 * declare-mode type switch — fill in the Unplanned contact fields, then flip
 * to Planned before saving — must not persist a notice onto the wrong event
 * type; the gate can't rely on the section simply not rendering anymore.
 *
 * Only the EPA contact is captured here. An outside 8700-12 filing is
 * recorded from the event's obligations row (`RecordNoticeDialog`), never
 * from this form.
 */
export function noticesToSave(
  values: EpisodicEventFormValues,
  defaults: EpisodicEventFormValues
): NoticeToSave[] {
  const notices: NoticeToSave[] = [];
  // A picked proof file — or an explicit proof removal — counts as dirty on
  // its own: changing only the proof of an otherwise-untouched notice must
  // still reach the server.
  if (
    values.type === WasteEpisodicEventType.Unplanned &&
    values.epaContacted === "yes" &&
    values.epaContactDate &&
    values.epaContactMethod &&
    (epaContactChanged(values, defaults) ||
      values.epaContactProofFile != null ||
      values.epaContactProofRemoved)
  ) {
    notices.push({
      kind: WasteEpisodicEventNoticeKind.EpaContact,
      noticeDate: values.epaContactDate,
      method: values.epaContactMethod,
      ...contactProofChange(values),
    });
  }
  return notices;
}

/**
 * Which recorded notices `saveNotices` should retract, because editing
 * flipped a prefilled "yes" to "no". A fresh declare has all-null defaults,
 * so this is always empty there — only an edit that walks back an existing
 * answer produces a deletion.
 */
export function noticesToDelete(
  values: EpisodicEventFormValues,
  defaults: EpisodicEventFormValues
): WasteEpisodicEventNoticeKind[] {
  const kinds: WasteEpisodicEventNoticeKind[] = [];
  if (defaults.epaContacted === "yes" && values.epaContacted !== "yes") {
    kinds.push(WasteEpisodicEventNoticeKind.EpaContact);
  }
  return kinds;
}

/** Upserts whichever notices `noticesToSave` says the form captured —
 * uploading a picked proof file first, so the upsert can attach its id — and
 * deletes whichever `noticesToDelete` says were walked back, after the event
 * itself is saved. */
function useSaveNotices() {
  const { tenantId } = useTenant();
  const { uploadProof } = useNoticeProofUpload();
  const [upsertNotice] = useMutation(UPSERT_EPISODIC_EVENT_NOTICE_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents"],
  });
  const [deleteNotice] = useMutation(DELETE_EPISODIC_EVENT_NOTICE_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents"],
  });

  return useCallback(
    async (eventId: string, values: EpisodicEventFormValues, defaults: EpisodicEventFormValues) => {
      for (const { proofFile, detachProof, ...notice } of noticesToSave(values, defaults)) {
        let documentInput: { documentId: string | null } | Record<string, never> = {};
        if (proofFile) {
          if (!tenantId) {
            throw new Error("Cannot upload without a tenant");
          }
          const uploaded = await uploadProof(proofFile, tenantId);
          documentInput = { documentId: uploaded.id };
        } else if (detachProof) {
          documentInput = { documentId: null };
        }
        await upsertNotice({ variables: { input: { eventId, ...notice, ...documentInput } } });
      }
      for (const kind of noticesToDelete(values, defaults)) {
        await deleteNotice({ variables: { eventId, kind } });
      }
    },
    [tenantId, uploadProof, upsertNotice, deleteNotice]
  );
}

/**
 * Owns the create/update mutations and the save-time validation gate (the
 * client-side reason and EPA-contact checks, then the server validator's
 * critical-issue gate) so the dialog component itself only wires the result
 * to the Save button. A notice-upsert failure after a successful event save
 * doesn't lose the event — it's caught and reported as its own toast rather
 * than surfacing as a failed save.
 *
 * `onSubmit` awaits the create/update mutation and then `finishAfterNotices`
 * itself, sequentially, rather than firing the notice writes from the
 * mutation's `onCompleted` — Apollo's `mutate()` promise resolves as soon as
 * `onCompleted` is *called*, not once whatever it kicks off finishes, so a
 * fire-and-forget `finishAfterNotices` there let Save go clickable again
 * while notice upserts/deletes were still in flight. `isFinishingNotices`
 * covers that window on top of the mutations' own `loading` flags, so `Save`
 * stays busy until the notices (and the resulting `onClose`) are done.
 */
function useEpisodicEventSave({
  mode,
  facilityId,
  event,
  containerTracking,
  defaultValues,
  getValues,
  setError,
  validateInput,
  onClose,
  onCreated,
}: {
  mode: "declare" | "edit";
  facilityId: string;
  event: EpisodicEventItemFragment | undefined;
  /** The values the dialog prefilled from `event.notices` — the baseline
   * `noticesToSave`/`noticesToDelete` diff the submitted values against. */
  containerTracking: boolean;
  defaultValues: EpisodicEventFormValues;
  getValues: () => EpisodicEventFormValues;
  setError: UseFormSetError<EpisodicEventFormValues>;
  validateInput: (values: EpisodicEventFormValues) => Promise<Issue[]>;
  onClose: () => void;
  onCreated?: (event: EpisodicEventItemFragment) => void;
}) {
  const alerts = useAlerts();
  const saveNotices = useSaveNotices();
  const [isFinishingNotices, setIsFinishingNotices] = useState(false);

  const refetchQueries = eventRefetchQueries(containerTracking);
  const [createEvent, { loading: isCreating }] = useMutation(CREATE_EPISODIC_EVENT_MUTATION, {
    refetchQueries,
  });
  const [updateEvent, { loading: isUpdating }] = useMutation(UPDATE_EPISODIC_EVENT_MUTATION, {
    refetchQueries,
  });

  const finishAfterNotices = async (
    eventId: string,
    values: EpisodicEventFormValues,
    done: () => void = onClose
  ) => {
    setIsFinishingNotices(true);
    try {
      try {
        await saveNotices(eventId, values, defaultValues);
        alerts.success(mode === "declare" ? "Episodic event declared" : "Episodic event updated");
      } catch {
        alerts.warning(
          "Event saved, but recording the EPA notice failed — record it from the event page"
        );
      }
      done();
    } finally {
      setIsFinishingNotices(false);
    }
  };

  const onSubmit = async () => {
    const values = getValues();
    if (!validateReason(values, setError)) {
      return;
    }
    if (!validateDates(values, setError)) {
      return;
    }
    if (!validateEpaContact(values, setError)) {
      return;
    }
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before saving");
      return;
    }
    if (mode === "declare") {
      let result;
      try {
        result = await createEvent({
          variables: { input: toCreateEpisodicEventInput(values, facilityId) },
        });
      } catch (error) {
        alerts.error(describeSaveError(error, "An error occurred while declaring the event"));
        return;
      }
      const createdEvent = result.data?.createEpisodicEvent;
      if (!createdEvent) {
        return;
      }
      await finishAfterNotices(
        createdEvent.id,
        values,
        onCreated ? () => onCreated(createdEvent) : onClose
      );
      return;
    }
    if (!event) {
      return;
    }
    try {
      await updateEvent({
        variables: { input: toUpdateEpisodicEventInput(values, event.id) },
      });
    } catch (error) {
      alerts.error(describeSaveError(error, "An error occurred while updating the event"));
      return;
    }
    await finishAfterNotices(event.id, values);
  };

  return { onSubmit, isSaving: isCreating || isUpdating || isFinishingNotices };
}

export type EpisodicEventDialogProps = {
  open: boolean;
  onClose: () => void;
  mode: "declare" | "edit";
  /** Every event belongs to exactly one facility; this locks the create form
   * to it and scopes the "one per type per year" validator check. */
  facilityId: string;
  /** Required in edit mode — the caller already has the full row from the
   * ledger, so this dialog doesn't re-fetch it. */
  event?: EpisodicEventItemFragment;
  initialValues?: Partial<Pick<EpisodicEventFormValues, "startDate" | "reason">>;
  onCreated?: (event: EpisodicEventItemFragment) => void;
};

/** The planned-vs-unplanned explainer at the top of the form. */
function TypeExplainer() {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        Planned or unplanned?
      </Typography>
      <InfoIconTooltip
        ariaLabel="Planned vs unplanned episodic events"
        tooltip={
          <Stack spacing={1} sx={{ p: 0.5 }}>
            <Typography variant="caption">
              <strong>Planned</strong> events are known in advance: tank cleanouts, equipment
              maintenance, excess inventory removal, short-term construction. EPA must be notified
              at least 30 days before the event starts.
            </Typography>
            <Typography variant="caption">
              <strong>Unplanned</strong> events are sudden: spills, process upsets, product recalls,
              acts of nature. EPA must be notified within 72 hours.
            </Typography>
            <Typography variant="caption">
              Either way, the event opens a 60-day window to ship its waste, the facility may run
              one of each per calendar year, and its generator status is unchanged.
            </Typography>
          </Stack>
        }
      />
    </Stack>
  );
}

export function EpisodicEventDialog({
  open,
  onClose,
  mode,
  facilityId,
  event,
  initialValues,
  onCreated,
}: EpisodicEventDialogProps) {
  const defaultValues = useMemo<EpisodicEventFormValues>(
    () => ({
      type: event?.type ?? WasteEpisodicEventType.Planned,
      reason: event?.reason ?? initialValues?.reason ?? "",
      startDate: event?.startDate ?? initialValues?.startDate ?? null,
      endDate: event?.endDate ?? null,
      ...epaContactDefaults(event?.notices ?? []),
      epaContactProofFile: null,
      epaContactProofRemoved: false,
    }),
    [event, initialValues]
  );

  const { featureFlags } = useFeatureFlags();
  const containerTracking = featureFlags[FeatureFlagKey.ContainerTracking] === true;
  const validateInput = useEpisodicEventInputValidation(facilityId, event?.id ?? null);
  const validateFormValues = useCallback(
    (values: EpisodicEventFormValues) =>
      validateInput(containerTracking ? values : withDerivedEndDate(values)),
    [validateInput, containerTracking]
  );
  const form = useValidatingForm<EpisodicEventFormValues>(defaultValues, [], validateFormValues);
  const { control, getValues, setError, issues: validatorIssues, trigger } = form;
  const issues = withoutSatisfiedFilingWarning(validatorIssues, event);

  // useValidatingForm's resolver only runs on RHF's own "onChange" trigger,
  // so without this the facility-level warnings (which don't need a date;
  // see useEpisodicEventInputValidation) wouldn't appear until the user
  // touched a field. Fire once when the dialog opens so they're visible
  // immediately.
  useEffect(() => {
    if (open) {
      // False positive: `trigger` is RHF's own form method, not a parent
      // callback — this is an imperative run-validation-on-mount call, not
      // data flowing up to a parent.
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      trigger();
    }
  }, [open, trigger]);

  const { onSubmit, isSaving } = useEpisodicEventSave({
    mode,
    facilityId,
    event,
    containerTracking,
    defaultValues,
    getValues: containerTracking ? getValues : () => withDerivedEndDate(getValues()),
    validateInput: validateFormValues,
    setError,
    onClose,
    onCreated,
  });

  const startDate = useWatch({ control, name: "startDate" });
  const startDateHelperText = startDate ? (relativeDayLabel(startDate) ?? undefined) : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={mode === "declare" ? "Declare episodic event" : "Edit episodic event"}
      primaryActionButtonProps={{
        loading: isSaving,
        onClick: onSubmit,
        // Deliberately NOT gated on form dirtiness: the type radio's default
        // made Save flicker enabled/disabled as the user toggled back to the
        // default value. Required fields are enforced at submit time instead
        // (validateReason/validateDates/validateEpaContact set field errors).
        disabled: hasCriticalIssues(issues),
      }}
    >
      <FormProvider {...form}>
        <Box component="form" sx={{ pt: 1 }} onSubmit={(event) => event.preventDefault()}>
          <TypeExplainer />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <RadioGroupField
                name="type"
                label=""
                control={control}
                radioOptions={TYPE_RADIO_OPTIONS}
                disabled={mode === "edit"}
              />
            </Grid>
            <YearLimitAlert issues={issues} />
            <Grid size={{ xs: 12 }}>
              <FormTextField
                name="reason"
                label="Reason"
                control={control}
                textFieldProps={{ required: true, multiline: true, minRows: 2 }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <DateField
                name="startDate"
                label="Start date"
                control={control}
                helperText={startDateHelperText}
              />
            </Grid>
            {containerTracking && (
              <Grid size={{ xs: 6 }}>
                <DateField name="endDate" label="End date" control={control} />
              </Grid>
            )}
            <IssueAlerts issues={issues} level={IssueLevel.Critical} severity="error" />
            <IssueAlerts issues={issues} level={IssueLevel.Error} severity="warning" />
            <EpaContactSection control={control} existingProof={epaContactProof(event)} />
          </Grid>
        </Box>
      </FormProvider>
    </Dialog>
  );
}
